// src/pages/auth/ChooseRole.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { readLastRole, writeLastRole } from "@/lib/roleCache";

const ROLES = new Set(["doctor", "patient"]);

const options = [
  {
    key: "doctor",
    title: "Doctor",
    desc: "Continue as a doctor to consult, prescribe, and manage your practice.",
    icon: "/icons/doctor1.svg",
    home: "/home/doctor",
  },
  {
    key: "patient",
    title: "Patient",
    desc: "Continue as a patient to book appointments and manage your health records.",
    icon: "/icons/patient1.svg",
    home: "/home/patient",
  },
];

export default function ChooseRole() {
  const nav = useNavigate();
  const auth = useAuth() || {};
  const { user, profile, loading } = auth;

  const hasDoctor = !!profile?.hasDoctor;
  const hasPatient = !!profile?.hasPatient;

  const [sel, setSel] = useState(() => readLastRole(user?.id));

  useEffect(() => {
    setSel(readLastRole(user?.id));
  }, [user?.id]);

  const valid = useMemo(() => ROLES.has(sel), [sel]);

  // Purposeful behavior:
  // - not authed → login
  // - not dual → go straight to the only home
  // - dual + last_role set → go straight there
  // - dual + no last_role → show chooser
  useEffect(() => {
    if (loading) return;

    if (!user?.id) {
      nav("/login", { replace: true });
      return;
    }

    // Not dual? Don’t show chooser.
    if (hasDoctor && !hasPatient) {
      writeLastRole("doctor", user?.id);
      nav("/home/doctor", { replace: true });
      return;
    }
    if (!hasDoctor && hasPatient) {
      writeLastRole("patient", user?.id);
      nav("/home/patient", { replace: true });
      return;
    }

    // Dual-role: if last role exists, respect it and don't show UI.
    if (hasDoctor && hasPatient) {
      const last = readLastRole(user?.id);
      if (last === "doctor") {
        nav("/home/doctor", { replace: true });
        return;
      }
      if (last === "patient") {
        nav("/home/patient", { replace: true });
        return;
      }
      // else: no last role → show chooser UI (do nothing here)
    }
  }, [loading, user?.id, hasDoctor, hasPatient, nav]);

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={1} total={1} onBack={() => nav("/home", { replace: true })} />

      <OnboardingSection
        header={
          <div className="space-y-2">
            <h2 className="text-title-large text-on-surface">Choose how you want to continue</h2>
            <p className="text-body-medium text-on-surface-var">
              We found both a Doctor and Patient profile on your account.
            </p>
          </div>
        }
        footer={
          <Button
            size="lg"
            disabled={!valid}
            onClick={() => {
              const opt = options.find((o) => o.key === sel);
              if (!opt) return;
              writeLastRole(sel, user?.id);
              nav(opt.home, { replace: true });
            }}
          >
            <span className="text-title-medium">Continue</span>
          </Button>
        }
      >
        <div className="space-y-3">
          {options.map((c) => {
            const selected = sel === c.key;

            return (
              <div key={c.key}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setSel(c.key)}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") && setSel(c.key)
                  }
                  className="relative w-full rounded-[12px] bg-white px-4 py-3 text-left flex items-start gap-4 pr-16"
                  style={{
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                  }}
                >
                  <img
                    src={c.icon}
                    alt=""
                    className="h-10 w-10 rounded-full mt-[2px] shrink-0"
                    draggable="false"
                  />
                  <div className="flex-1">
                    <div className="text-body-large text-on-surface">{c.title}</div>
                    <p className="mt-1 text-body-medium text-on-surface-var">{c.desc}</p>
                  </div>

                  {/* Radio */}
                  <span
                    aria-hidden
                    className={[
                      "pointer-events-none absolute right-4 top-3 grid place-items-center rounded-full h-6 w-6",
                      selected
                        ? "border-2 border-[rgb(var(--afrimed-primary))]"
                        : "border border-[rgb(var(--clr-outline))]",
                    ].join(" ")}
                  >
                    <span
                      className={
                        selected
                          ? "h-2.5 w-2.5 rounded-full bg-[rgb(var(--afrimed-primary))]"
                          : "h-2.5 w-2.5 rounded-full bg-transparent"
                      }
                    />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </OnboardingSection>
    </div>
  );
}
